import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/use-auth";

function initialsFor(firstName?: string, lastName?: string): string {
  const f = firstName?.trim()[0] ?? "";
  const l = lastName?.trim()[0] ?? "";
  return (f + l).toUpperCase() || "?";
}

/**
 * 30px avatar circle with the user's initials. Click to open a small dropdown
 * with a "Sign out" action. Matches prototype `Class Scheduler.dc.html:81-84`.
 */
export function UserAvatarMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const initials = initialsFor(user?.firstName, user?.lastName);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="user-avatar-button"
        className="w-[30px] h-[30px] rounded-full bg-surface-2 flex items-center justify-center text-xs font-semibold text-text-soft hover:bg-surface-2/80 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-surface"
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-44 bg-surface border border-border-subtle rounded-md shadow-lg overflow-hidden z-30"
        >
          <div className="px-3 py-2 border-b border-border-subtle">
            <div className="text-xs text-text-muted">Signed in as</div>
            <div className="text-sm font-medium text-white truncate">
              {user?.fullName || user?.email}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            data-testid="sign-out-button"
            className="w-full text-left px-3 py-2 text-sm text-text-soft hover:bg-surface-2/70"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
